<template>
  <v-app>
    <!-- Top Navigation Bar -->
    <v-app-bar :color="colors.primaryDark" dark elevation="4" height="60">
      <div class="d-flex align-center">
        <v-icon size="40" class="ml-7 mr-2 pulse">fas fa-building</v-icon>
        <span class="text-h6 font-weight-bold gradient-text">
          SICON PAINEL DE CONTROLE I.A
        </span>
      </div>
      <v-spacer></v-spacer>
      <div class="d-flex align-center" style="gap: 24px">
        <!-- User Profile -->
        <div class="d-flex align-center mr-12 user-profile" style="gap: 7px">
          <v-avatar size="40" :color="colors.primaryLight" class="profile-avatar">
            <v-icon color="white" size="20">fas fa-user</v-icon>
          </v-avatar>
          <div class="d-flex flex-column">
            <span class="font-weight-bold text-body-2">
              {{ userDetails?.name || 'Carregando...' }}
            </span>
            <span class="text-caption opacity-75">
              {{ userDetails?.department?.name || 'Online' }}
            </span>
          </div>
        </div>
      </div>
    </v-app-bar>

    <!-- Sidebar Navigation -->
    <v-navigation-drawer
      v-model="drawer"
      :color="colors.primaryDark"
      dark
      permanent
      width="250"
      class="custom-drawer"
    >
      <!-- User Profile in Sidebar -->
      <div class="pa-4 sidebar-profile" style="border-bottom: 1px solid #1a4b7a">
        <div class="d-flex align-center" style="gap: 12px">
          <v-avatar size="45" :color="colors.primaryLight" class="profile-avatar">
            <v-icon color="white" size="22">fas fa-user</v-icon>
          </v-avatar>
          <div>
            <div>
              <div>
                <div class="font-weight-bold text-body-2 lowercase">
                  {{ userDetails?.name || 'Carregando...' }}
                </div>
              </div>
            </div>
            <div class="text-caption opacity-75">
              {{ userDetails?.department?.name || 'Sistema Online' }}
            </div>
          </div>
        </div>
      </div>

      <!-- Navigation Menu -->
      <v-list nav class="pa-0 text-start">
        <v-list-item
          v-for="item in menuItems"
          :key="item.title"
          :value="item.value"
          :active="item.active"
          rounded="xl"
          class="mb-2 menu-item"
          :class="item.active ? 'active-item' : ''"
          lines="one"
          density="comfortable"
          @click="router.push(item.route)"
        >
          <template #prepend>
            <v-icon size="22" class="mr-2">{{ item.icon }}</v-icon>
          </template>

          <template #title>
            <span class="item-title">{{ item.title }}</span>
          </template>

          <template #append v-if="item.badge">
            <v-chip size="small" :color="item.badgeColor" class="pulse-badge">
              {{ item.badge }}
            </v-chip>
          </template>
        </v-list-item>
      </v-list>

      <template v-slot:append>
        <div class="pa-3" style="border-top: 1px solid #1a4b7a">
          <v-btn
            block
            variant="outlined"
            prepend-icon="fas fa-sign-out-alt"
            class="logout-btn"
            @click="logout"
          >
            Sair
          </v-btn>
        </div>
      </template>
    </v-navigation-drawer>

    <!-- Main Content -->
    <v-main class="main-content" style="margin-top: 20px">
      <v-container fluid class="pa-4">
        <!-- Page Header -->
        <div class="d-flex justify-space-between align-center mb-8">
          <div class="d-flex align-center" style="gap: 16px">
            <v-btn
              :color="colors.primaryLight"
              prepend-icon="fas fa-users"
              elevation="3"
              class="header-btn"
            >
              <b style="color: #ffff">Gerenciar Usuários</b>
            </v-btn>
            <v-chip :color="colors.primaryGreen" size="large" class="status-chip">
              <v-icon start size="18">fas fa-chart-line</v-icon>
              {{ Usuarios.length }} usuários cadastrados
            </v-chip>
          </div>
          <v-btn
            :color="colors.primaryGreen"
            prepend-icon="fas fa-plus"
            elevation="3"
            class="add-btn"
            @click="adicionarUsuario"
          >
            Novo Usuário
          </v-btn>
        </div>

        <!-- Lista de Usuarios -->
        <v-row>
          <v-col cols="12" lg="10" class="mx-auto">
            <v-card elevation="8" class="rounded-xl modern-card">
              <!-- Header do Card -->
              <div class="card-header pa-6" :style="{ background: `linear-gradient(135deg, ${colors.primaryLight}, ${colors.primaryDark})` }">
                <div class="d-flex align-center">
                  <div class="header-icon-wrapper mr-4">
                    <v-icon color="white" size="28">fas fa-users</v-icon>
                  </div>
                  <div class="flex-grow-1">
                    <h2 class="text-white font-weight-bold mb-1">Lista de Usuários</h2>
                    <p class="text-white opacity-90 mb-0">Gerencie todos os usuários do sistema</p>
                  </div>
                  <v-chip color="white" text-color="primary" size="large" class="stats-chip">
                    <v-icon start>fas fa-user-check</v-icon>
                    {{ Usuarios.length }}
                  </v-chip>
                </div>
              </div>

              <!-- Search Section -->
              <div class="pa-6 pb-0">
                <v-text-field
                  v-model="filtroNome"
                  placeholder="Buscar por nome ou função..."
                  prepend-inner-icon="fas fa-search"
                  variant="outlined"
                  density="comfortable"
                  hide-details
                  clearable
                  class="search-field"
                  :color="colors.primaryLight"
                >
                  <template v-slot:append-inner>
                    <v-fade-transition>
                      <v-icon v-if="filtroNome" color="success">fas fa-filter</v-icon>
                    </v-fade-transition>
                  </template>
                </v-text-field>
              </div>

              <!-- Loading State -->
              <div v-if="loading" class="text-center pa-8">
                <v-progress-circular
                  :color="colors.primaryLight"
                  indeterminate
                  size="60"
                  width="6"
                ></v-progress-circular>
                <p class="text-h6 mt-4" style="color: #123A5D">Carregando usuários...</p>
              </div>

              <!-- User List -->
              <div v-else class="user-list-container">
                <v-list class="pa-0" v-if="UsuariosFiltrados.length > 0">
                  <template v-for="(usuario, index) in UsuariosFiltrados" :key="usuario.id">
                    <v-list-item
                      class="user-item pa-6"
                      :class="{ 'alternate-bg': index % 2 === 0 }"
                      @click="navegarParaDetalhes(usuario.id)"
                    >
                      <template v-slot:prepend>
                        <div class="user-avatar-container mr-4">
                          <v-avatar :color="getStatusColor(usuario.status)" size="56" class="user-avatar">
                            <v-icon color="white" size="24">fas fa-user</v-icon>
                          </v-avatar>
                          <v-badge
                            :color="getStatusBadgeColor(usuario.status)"
                            :content="isUserActive(usuario.status) ? '●' : '○'"
                            justify="left"
                            offset-x="15"
                            offset-y="8"
                          ></v-badge>
                        </div>
                      </template>
                      
                      <div class="user-info flex-grow-1">
                        <div class="d-flex align-center mb-2">
                          <h3 class="user-name text-h6 font-weight-bold mr-3">
                            {{ usuario.name }}
                          </h3>
                          <v-chip
                            :color="isUserActive(usuario.status) ? 'success' : 'error'"
                            size="small"
                            variant="tonal"
                            class="status-indicator"
                          >
                            <v-icon start size="12">
                              {{ isUserActive(usuario.status) ? 'fas fa-check-circle' : 'fas fa-times-circle' }}
                            </v-icon>
                            {{ isUserActive(usuario.status) ? 'Ativo' : 'Inativo' }}
                          </v-chip>
                        </div>
                        
                        <div class="d-flex align-center mb-2">
                          <v-icon :color="colors.primaryDark" size="16" class="mr-2">fas fa-envelope</v-icon>
                          <span class="text-body-2" style="color: #123A5D">{{ usuario.email || 'Email não informado' }}</span>
                        </div>

                        <div class="roles-container" v-if="usuario.roles && usuario.roles.length">
                          <v-chip
                            v-for="role in usuario.roles"
                            :key="role"
                            :color="getRoleColor(role)"
                            size="small"
                            variant="outlined"
                            class="mr-2 role-chip"
                          >
                            <v-icon start size="12">{{ getRoleIcon(role) }}</v-icon>
                            {{ role.toUpperCase() }}
                          </v-chip>
                        </div>
                      </div>

                      <template v-slot:append>
                        <div class="action-buttons d-flex align-center" style="gap: 12px">
                          <!-- Status Switch -->
                          <div class="switch-container">
                            <v-switch
                              :model-value="isUserActive(usuario.status)"
                              @update:model-value="(value) => toggleUserStatus(usuario, value)"
                              color="success"
                              inset
                              hide-details
                              @click.stop
                              class="status-switch"
                              :loading="usuario.updating"
                              :true-icon="'fas fa-check'"
                              :false-icon="'fas fa-times'"
                            >
                              <template v-slot:thumb>
                                <v-icon 
                                  :color="isUserActive(usuario.status) ? 'success' : 'error'" 
                                  size="16"
                                >
                                  {{ isUserActive(usuario.status) ? 'fas fa-check' : 'fas fa-times' }}
                                </v-icon>
                              </template>
                            </v-switch>
                          </div>

                          <v-tooltip text="Editar usuário" location="top">
                            <template v-slot:activator="{ props }">
                              <v-btn
                                v-bind="props"
                                icon
                                variant="tonal"
                                size="small"
                                :color="colors.primaryLight"
                                @click.stop="editarUsuario(usuario)"
                                class="action-btn edit-btn"
                              >
                                <v-icon size="18">fas fa-edit</v-icon>
                              </v-btn>
                            </template>
                          </v-tooltip>
                        </div>
                      </template>
                    </v-list-item>
                    
                    <v-divider v-if="index < UsuariosFiltrados.length - 1" class="mx-6" :color="colors.primaryLight" opacity="0.3"></v-divider>
                  </template>
                </v-list>

                <!-- Empty State -->
                <div v-else class="empty-state text-center pa-12">
                  <div class="empty-icon mb-6">
                    <v-icon size="80" :color="colors.primaryLight" class="opacity-60">
                      {{ filtroNome ? 'fas fa-search' : 'fas fa-users-slash' }}
                    </v-icon>
                  </div>
                  <h3 class="text-h5 font-weight-bold mb-3" style="color: #123A5D">
                    {{ filtroNome ? 'Nenhum usuário encontrado' : 'Nenhum usuário cadastrado' }}
                  </h3>
                  <p class="text-body-1 mb-6" style="color: #57C2E3">
                    {{ filtroNome ? 'Tente ajustar os termos de busca ou limpar o filtro' : 'Comece adicionando o primeiro usuário ao sistema' }}
                  </p>
                  <v-btn
                    v-if="filtroNome"
                    :color="colors.primaryLight"
                    prepend-icon="fas fa-times"
                    variant="outlined"
                    @click="filtroNome = ''"
                    class="clear-filter-btn"
                  >
                    Limpar Filtro
                  </v-btn>
                  <v-btn
                    v-else
                    :color="colors.primaryGreen"
                    prepend-icon="fas fa-plus"
                    elevation="2"
                    class="add-first-btn"
                    @click="adicionarUsuario"
                  >
                    Adicionar Primeiro Usuário
                  </v-btn>
                </div>
              </div>

              <!-- Card Footer -->
              <v-card-actions class="pa-6 border-t" style="background: #E7F2F9; border-color: #57C2E3">
                <div class="d-flex align-center w-100">
                  <div class="d-flex align-center">
                    <v-icon :color="colors.primaryLight" class="mr-2">fas fa-info-circle</v-icon>
                    <span class="text-body-2" style="color: #123A5D">
                      Mostrando {{ UsuariosFiltrados.length }} de {{ Usuarios.length }} usuários
                    </span>
                  </div>
                  <v-spacer></v-spacer>
                  <div class="d-flex align-center" style="gap: 16px">
                    <v-btn
                      :color="colors.primaryGreen"
                      prepend-icon="fas fa-sync-alt"
                      variant="tonal"
                      size="small"
                      @click="carregarUsuarios"
                      class="refresh-btn"
                      :loading="loading"
                    >
                      Atualizar
                    </v-btn>
                  </div>
                </div>
              </v-card-actions>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </v-main>

    <!-- Modal de Edição de Usuário -->
    <v-dialog
      v-model="editDialog.show"
      max-width="900px"
      persistent
      class="edit-user-dialog"
    >
      <v-card class="modern-edit-card" elevation="24">
        <!-- Header do Modal -->
        <div class="edit-card-header pa-6" :style="{ background: `linear-gradient(135deg, ${colors.primaryLight}, ${colors.primaryDark})` }">
          <div class="d-flex align-center">
            <div class="header-icon-wrapper mr-4">
              <v-icon color="white" size="24">fas fa-user-edit</v-icon>
            </div>
            <div class="flex-grow-1">
              <h2 class="text-white font-weight-bold mb-1">Editar Usuário</h2>
              <p class="text-white opacity-90 mb-0">{{ editDialog.user?.name || 'Usuário' }}</p>
            </div>
            <v-btn
              icon
              variant="text"
              color="white"
              @click="fecharModalEdicao"
              class="close-btn"
            >
              <v-icon>fas fa-times</v-icon>
            </v-btn>
          </div>
        </div>

        <!-- Conteúdo do Modal -->
        <v-card-text class="pa-6">
          <v-form ref="editForm" v-model="editDialog.valid">
            <v-row>
              <!-- Informações Básicas -->
              <v-col cols="12">
                <h3 class="text-h6 font-weight-bold mb-4" style="color: #123A5D">
                  <v-icon :color="colors.primaryLight" class="mr-2">fas fa-user</v-icon>
                  Informações Básicas
                </h3>
              </v-col>

              <!-- Email -->
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="editDialog.formData.email"
                  label="Email"
                  prepend-inner-icon="fas fa-envelope"
                  variant="outlined"
                  density="comfortable"
                  :color="colors.primaryLight"
                  :rules="[rules.required, rules.email]"
                  type="email"
                  class="edit-field"
                />
              </v-col>

              <!-- Role -->
              <v-col cols="12" md="6">
                  <v-select
                    v-model="editDialog.formData.role"
                    :items="roleOptions"
                    label="Função/Role"
                    prepend-inner-icon="fas fa-user-tag"
                    variant="outlined"
                    density="comfortable"
                    :color="colors.primaryLight"
                    :rules="[rules.required]"
                  />

                </v-col>

                <!-- ADICIONE ESTE NOVO CAMPO DE DEPARTAMENTO AQUI -->
                <v-col cols="12" md="6">
                  <v-select
                    v-model="createDialog.formData.departmentId"
                    :items="departments"
                    item-title="title"
                    item-value="value"
                    label="Departamento"
                    prepend-inner-icon="fas fa-building"
                    variant="outlined"
                    density="comfortable"
                    :color="colors.primaryLight"
                    :rules="[createRules.required]"
                    class="create-field"
                    clearable
                    no-data-text="Nenhum departamento encontrado"
                  />
                </v-col>

              <!-- Telas Disponíveis -->
              <v-col cols="12">
                <h3 class="text-h6 font-weight-bold mb-4" style="color: #123A5D">
                  <v-icon :color="colors.primaryGreen" class="mr-2">fas fa-tv</v-icon>
                  Telas Disponíveis
                </h3>
                <p class="text-body-2 mb-4" style="color: #57C2E3">
                  Selecione quais telas este usuário pode acessar
                </p>
                
                <v-row>
                  <v-col
                    v-for="screen in availableScreens"
                    :key="screen.value"
                    cols="12"
                    sm="6"
                    md="4"
                  >
                    <v-card
                      class="screen-card"
                      :class="{ 'screen-selected': editDialog.formData.allowedScreens.includes(screen.value) }"
                      @click="toggleScreen(screen.value)"
                      elevation="2"
                    >
                      <v-card-text class="pa-4 text-center">
                        <v-icon
                          :color="editDialog.formData.allowedScreens.includes(screen.value) ? colors.primaryGreen : 'grey'"
                          size="32"
                          class="mb-2"
                        >
                          {{ screen.icon }}
                        </v-icon>
                        <div class="font-weight-medium" style="color: #123A5D">
                          {{ screen.title }}
                        </div>
                        <v-chip
                          v-if="editDialog.formData.allowedScreens.includes(screen.value)"
                          color="success"
                          size="small"
                          class="mt-2"
                        >
                          <v-icon start size="12">fas fa-check</v-icon>
                          Selecionado
                        </v-chip>
                      </v-card-text>
                    </v-card>
                  </v-col>
                </v-row>

                <!-- Ações rápidas para telas -->
                <div class="d-flex align-center mt-4" style="gap: 12px">
                  <v-btn
                    :color="colors.primaryGreen"
                    variant="tonal"
                    size="small"
                    prepend-icon="fas fa-check-double"
                    @click="selecionarTodasTelas"
                  >
                    Selecionar Todas
                  </v-btn>
                  <v-btn
                    :color="colors.primaryLight"
                    variant="tonal"
                    size="small"
                    prepend-icon="fas fa-times"
                    @click="limparTelas"
                  >
                    Limpar Seleção
                  </v-btn>
                  <v-spacer></v-spacer>
                  <v-chip :color="colors.primaryDark" variant="tonal">
                    {{ editDialog.formData.allowedScreens.length }} tela(s) selecionada(s)
                  </v-chip>
                </div>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>

        <!-- Footer do Modal -->
        <v-card-actions class="pa-6 border-t" style="background: #E7F2F9; border-color: #57C2E3">
          <div class="d-flex align-center w-100">
            <div class="d-flex align-center">
              <v-icon :color="colors.primaryLight" class="mr-2">fas fa-info-circle</v-icon>
              <span class="text-body-2" style="color: #123A5D">
                Alterações serão aplicadas imediatamente
              </span>
            </div>
            <v-spacer></v-spacer>
            <div class="d-flex align-center" style="gap: 12px">
              <v-btn
                variant="outlined"
                @click="fecharModalEdicao"
                class="cancel-btn"
              >
                Cancelar
              </v-btn>
              <v-btn
                :color="colors.primaryGreen"
                :loading="editDialog.loading"
                @click="salvarEdicao"
                class="save-btn"
                :disabled="!editDialog.valid"
              >
                <v-icon start>fas fa-save</v-icon>
                Salvar Alterações
              </v-btn>
            </div>
          </div>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Modal de Criação de Usuário -->
    <v-dialog
      v-model="createDialog.show"
      max-width="900px"
      persistent
      class="create-user-dialog"
    >
      <v-card class="modern-create-card" elevation="24">
        <!-- Header do Modal -->
        <div class="create-card-header pa-6" :style="{ background: `linear-gradient(135deg, ${colors.primaryGreen}, ${colors.primaryDark})` }">
          <div class="d-flex align-center">
            <div class="header-icon-wrapper mr-4">
              <v-icon color="white" size="24">fas fa-user-plus</v-icon>
            </div>
            <div class="flex-grow-1">
              <h2 class="text-white font-weight-bold mb-1">Criar Novo Usuário</h2>
              <p class="text-white opacity-90 mb-0">Adicione um novo usuário ao sistema</p>
            </div>
            <v-btn
              icon
              variant="text"
              color="white"
              @click="fecharModalCriacao"
              class="close-btn"
            >
              <v-icon>fas fa-times</v-icon>
            </v-btn>
          </div>
        </div>

        <!-- Conteúdo do Modal -->
        <v-card-text class="pa-6">
          <v-form ref="createForm" v-model="createDialog.valid">
            <v-row>
              <!-- Informações Básicas -->
              <v-col cols="12">
                <h3 class="text-h6 font-weight-bold mb-4" style="color: #123A5D">
                  <v-icon :color="colors.primaryLight" class="mr-2">fas fa-user</v-icon>
                  Informações Básicas
                </h3>
              </v-col>

              <!-- Nome Completo -->
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="createDialog.formData.name"
                  label="Nome Completo"
                  prepend-inner-icon="fas fa-user"
                  variant="outlined"
                  density="comfortable"
                  :color="colors.primaryLight"
                  :rules="[createRules.required]"
                  class="create-field"
                  counter="100"
                  maxlength="100"
                />
              </v-col>

              <!-- Email -->
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="createDialog.formData.email"
                  label="Email"
                  prepend-inner-icon="fas fa-envelope"
                  variant="outlined"
                  density="comfortable"
                  :color="colors.primaryLight"
                  :rules="[createRules.required, createRules.email]"
                  type="email"
                  class="create-field"
                />
              </v-col>

              <!-- Senha -->
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="createDialog.formData.password"
                  label="Senha"
                  prepend-inner-icon="fas fa-lock"
                  variant="outlined"
                  density="comfortable"
                  :color="colors.primaryLight"
                  :rules="[createRules.required, createRules.minLength]"
                  type="password"
                  class="create-field"
                  hint="Mínimo de 6 caracteres"
                />
              </v-col>

              <!-- Confirmar Senha -->
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="createDialog.formData.confirmPassword"
                  label="Confirmar Senha"
                  prepend-inner-icon="fas fa-lock"
                  variant="outlined"
                  density="comfortable"
                  :color="colors.primaryLight"
                  :rules="[createRules.required, createRules.confirmPassword]"
                  type="password"
                  class="create-field"
                  hint="Digite a senha novamente"
                />
              </v-col>

              <v-col cols="12" md="6">
                  <v-select
                    v-model="createDialog.formData.departmentId"
                    :items="departments"
                    label="Departamento"
                    prepend-inner-icon="fas fa-building"
                    variant="outlined"
                    density="comfortable"
                    :color="colors.primaryLight"
                    :rules="[createRules.required]"
                    class="create-field"
                    clearable
                    no-data-text="Nenhum departamento encontrado"
                  />
                </v-col>

              <!-- Role -->
              <v-col cols="12" md="6">
                <v-select
                  v-model="createDialog.formData.role"
                  :items="roleOptions"
                  label="Função/Role"
                  prepend-inner-icon="fas fa-user-tag"
                  variant="outlined"
                  density="comfortable"
                  :color="colors.primaryLight"
                  :rules="[createRules.required]"
                  class="create-field"
                />
              </v-col>

              <!-- Status Inicial -->
              <v-col cols="12" md="6">
                <div class="d-flex align-center h-100">
                  <v-icon :color="colors.primaryGreen" class="mr-3">fas fa-power-off</v-icon>
                  <div>
                    <p class="font-weight-medium mb-1" style="color: #123A5D">Status Inicial</p>
                    <v-chip color="success" size="small">
                      <v-icon start size="12">fas fa-check-circle</v-icon>
                      Usuário será criado como ATIVO
                    </v-chip>
                  </div>
                </div>
              </v-col>

              <!-- Telas Disponíveis -->
              <v-col cols="12">
                <h3 class="text-h6 font-weight-bold mb-4" style="color: #123A5D">
                  <v-icon :color="colors.primaryGreen" class="mr-2">fas fa-tv</v-icon>
                  Telas Disponíveis
                </h3>
                <p class="text-body-2 mb-4" style="color: #57C2E3">
                  Selecione quais telas este usuário pode acessar
                </p>
                
                <v-row>
                  <v-col
                    v-for="screen in availableScreens"
                    :key="screen.value"
                    cols="12"
                    sm="6"
                    md="4"
                  >
                    <v-card
                      class="screen-card"
                      :class="{ 'screen-selected': createDialog.formData.allowedScreens.includes(screen.value) }"
                      @click="toggleScreenCreate(screen.value)"
                      elevation="2"
                    >
                      <v-card-text class="pa-4 text-center">
                        <v-icon
                          :color="createDialog.formData.allowedScreens.includes(screen.value) ? colors.primaryGreen : 'grey'"
                          size="32"
                          class="mb-2"
                        >
                          {{ screen.icon }}
                        </v-icon>
                        <div class="font-weight-medium" style="color: #123A5D">
                          {{ screen.title }}
                        </div>
                        <v-chip
                          v-if="createDialog.formData.allowedScreens.includes(screen.value)"
                          color="success"
                          size="small"
                          class="mt-2"
                        >
                          <v-icon start size="12">fas fa-check</v-icon>
                          Selecionado
                        </v-chip>
                      </v-card-text>
                    </v-card>
                  </v-col>
                </v-row>

                <!-- Ações rápidas para telas -->
                <div class="d-flex align-center mt-4" style="gap: 12px">
                  <v-btn
                    :color="colors.primaryGreen"
                    variant="tonal"
                    size="small"
                    prepend-icon="fas fa-check-double"
                    @click="selecionarTodasTelasCreate"
                  >
                    Selecionar Todas
                  </v-btn>
                  <v-btn
                    :color="colors.primaryLight"
                    variant="tonal"
                    size="small"
                    prepend-icon="fas fa-times"
                    @click="limparTelasCreate"
                  >
                    Limpar Seleção
                  </v-btn>
                  <v-spacer></v-spacer>
                  <v-chip :color="colors.primaryDark" variant="tonal">
                    {{ createDialog.formData.allowedScreens.length }} tela(s) selecionada(s)
                  </v-chip>
                </div>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>

        <!-- Footer do Modal -->
        <v-card-actions class="pa-6 border-t" style="background: #E7F2F9; border-color: #57C2E3">
          <div class="d-flex align-center w-100">
            <div class="d-flex align-center">
              <v-icon :color="colors.primaryGreen" class="mr-2">fas fa-info-circle</v-icon>
              <span class="text-body-2" style="color: #123A5D">
                O usuário receberá um email com suas credenciais
              </span>
            </div>
            <v-spacer></v-spacer>
            <div class="d-flex align-center" style="gap: 12px">
              <v-btn
                variant="outlined"
                @click="fecharModalCriacao"
                class="cancel-btn"
              >
                Cancelar
              </v-btn>
              <v-btn
                :color="colors.primaryGreen"
                :loading="createDialog.loading"
                @click="criarUsuario"
                class="create-btn"
                :disabled="!createDialog.valid"
              >
                <v-icon start>fas fa-user-plus</v-icon>
                Criar Usuário
              </v-btn>
            </div>
          </div>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar for notifications -->
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="snackbar.timeout"
      location="top right"
    >
      {{ snackbar.message }}
      <template v-slot:actions>
        <v-btn color="white" variant="text" @click="snackbar.show = false">
          Fechar
        </v-btn>
      </template>
    </v-snackbar>
  </v-app>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from "vue";
import axios from "axios";
import router from "@/router";
import { menuItems,  } from '@/constants/MenuItems'
import { useRouter } from 'vue-router';
import { useAuthStore } from '@/stores/auth'
import { usePermissions } from '@/composables/usePermissions';

const API_BASE = (process.env.VUE_APP_API_URL || '').replace(/\/$/, '');

const departments = ref([]);

const createDialog = ref({
  show: false,
  loading: false,
  valid: false,
  formData: {
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    role: 'user',
    departmentId: null,
    allowedScreens: []
  }
});

const { 
  userDetails, 
  checkPermissionAndRedirect
} = usePermissions();

const checkAccessAndInitialize = async () => {
  const hasAccess = await checkPermissionAndRedirect('residents');
  if (!hasAccess) {
    return false; // Redireciona para /access-denied
  }
  
  // Se tem permissão, carrega o conteúdo
  isMounted.value = true;
  await carregarDepartamentos();
  return true;
};

const carregarDepartamentos = async () => {
  if (!token.value) return;

  try {
    const response = await axios.get(
      `${API_BASE}/departments`,
      {
        headers: { Authorization: `Bearer ${token.value}` }
      }
    );

    departments.value = response.data.map(dept => ({
      title: dept.name,
      value: dept.id // cuid (String)
    }));
  } catch (error) {
    console.error('Erro ao carregar departamentos:', error);
    departments.value = [];
    showSnackbar('Não foi possível carregar departamentos', 'error');
  }
};


const createRules = {
  required: value => !!value || 'Campo obrigatório',
  email: value => {
    const pattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return pattern.test(value) || 'Email inválido';
  },
  minLength: value => value?.length >= 6 || 'Mínimo de 6 caracteres',
  confirmPassword: value => {
    return value === createDialog.value.formData.password || 'Senhas não coincidem';
  }
};

const availableScreens = [
  {
    title: "Reportes da IA",
    value: "ticket",
    icon: "fa-solid fa-robot"
  },
  {
    title: "Arquivos",
    value: "files",
    icon: "fa-solid fa-file"
  },
  {
    title: "Configurações da IA",
    value: "config",
    icon: "fa-solid fa-gear"
  },
  {
    title: "Transferências",
    value: "transfers",
    icon: "fas fa-exchange-alt"
  },
  {
    title: "Financeiro",
    value: "financer",
    icon: "fas fa-file-invoice-dollar"
  },
  {
    title: "Usuários",
    value: "residents",
    icon: "fas fa-users"
  },
  {
    title: "Tarefas",
    value: "calendar",
    icon: "fas fa-tasks"
  },
  {
    title: "Chat",
    value: "settings",
    icon: "fa-solid fa-comment"
  },
  {
    title: "Conexões WhatsApp",
    value: "config-wpp",
    icon: "fa-brands fa-square-whatsapp"
  },
  {
    title: "Sicon GPT",
    value: "sicon-gpt",
    icon: "fa-solid fa-comments"
  }
];

// Também adicione a função selecionarTodasTelas que estava faltando:
const selecionarTodasTelas = () => {
  editDialog.value.formData.allowedScreens = availableScreens.map(screen => screen.value);
};
// Router instance
const routerInstance = useRouter();

// Reactive data
const drawer = ref(true);
const filtroNome = ref("");
const loading = ref(false);
const isMounted = ref(false);

// Auth store
const authStore = useAuthStore();
const token = computed(() => authStore?.token || null);

// Snackbar for notifications
const snackbar = ref({
  show: false,
  message: '',
  color: 'success',
  timeout: 4000
});

// Color scheme
const colors = {
  primaryDark: "#123A5D",
  primaryLight: "#57C2E3",
  primaryGreen: "#A3C940",
  lightBg: "#E7F2F9",
};

// Dados dos usuários
const Usuarios = ref([]);

// Modal de edição
const editDialog = ref({
  show: false,
  loading: false,
  valid: false,
  user: null,
  formData: {
    email: '',
    role: '',
    allowedScreens: []
  }
});

// Opções de role
const roleOptions = [
  { title: 'Administrador', value: 'admin' },
  { title: 'Gerente', value: 'manager' },
  { title: 'Usuário', value: 'user' },
  { title: 'Convidado', value: 'guest' }
];

// Telas disponíveis baseadas no menuItems
// Regras de validação
const rules = {
  required: value => !!value || 'Campo obrigatório',
  email: value => {
    const pattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return pattern.test(value) || 'Email inválido';
  }
};

// HELPER FUNCTIONS
const isUserActive = (status) => {
  if (typeof status === 'boolean') {
    return status;
  }
  if (typeof status === 'string') {
    return status.toLowerCase() === 'true' || status === '1' || status.toLowerCase() === 'ativo';
  }
  if (typeof status === 'number') {
    return status === 1;
  }
  return false;
};

const getStatusColor = (status) => {
  return isUserActive(status) ? '#4CAF50' : '#F44336';
};

const getStatusBadgeColor = (status) => {
  return isUserActive(status) ? 'success' : 'error';
};

// Configuração do axios com interceptors
const setupAxiosInterceptors = () => {
  axios.interceptors.request.use(
    (config) => {
      const currentToken = token.value;
      if (currentToken) {
        config.headers.Authorization = `Bearer ${currentToken}`;
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response?.status === 401) {
        authStore.logout();
        routerInstance.push('/login');
        showSnackbar('Sessão expirada. Faça login novamente.', 'error');
      }
      return Promise.reject(error);
    }
  );
};


// Helper para mostrar notificações
const showSnackbar = (message, color = 'success', timeout = 4000) => {
  snackbar.value = {
    show: true,
    message,
    color,
    timeout
  };
};

// Fetch user details


// Computed para filtrar usuários
const UsuariosFiltrados = computed(() => {
  if (!filtroNome.value) return Usuarios.value;
  
  return Usuarios.value.filter(usuario => {
    const nome = usuario.name?.toLowerCase() || '';
    const email = usuario.email?.toLowerCase() || '';
    const roles = usuario.roles?.join(' ').toLowerCase() || '';
    const filtro = filtroNome.value.toLowerCase();
    
    return nome.includes(filtro) || email.includes(filtro) || roles.includes(filtro);
  });
});

// Métodos de navegação e ações
const navegarParaDetalhes = (userId) => {
  routerInstance.push(`/usuario/editar/${userId}`);
};

const adicionarUsuario = () => {
  // Em vez de navegar, abra o modal
  createDialog.value = {
    show: true,
    loading: false,
    valid: false,
    formData: {
      name: '',
      email: '',
      password: '',
      confirmPassword: '',
      role: 'user',
      departmentId: null,
      allowedScreens: []
    }
  };
};
const fecharModalCriacao = () => {
  createDialog.value = {
    show: false,
    loading: false,
    valid: false,
    formData: {
      name: '',
      email: '',
      password: '',
      confirmPassword: '',
      role: 'user',
      allowedScreens: []
    }
  };
};

const toggleScreenCreate = (screenValue) => {
  const index = createDialog.value.formData.allowedScreens.indexOf(screenValue);
  if (index > -1) {
    createDialog.value.formData.allowedScreens.splice(index, 1);
  } else {
    createDialog.value.formData.allowedScreens.push(screenValue);
  }
};

const selecionarTodasTelasCreate = () => {
  createDialog.value.formData.allowedScreens = availableScreens.map(screen => screen.value);
};

const limparTelasCreate = () => {
  createDialog.value.formData.allowedScreens = [];
};

const criarUsuario = async () => {
  if (!createDialog.value.valid) {
    showSnackbar('Preencha todos os campos obrigatórios', 'error');
    return;
  }

  if (!createDialog.value.formData.departmentId) {
    showSnackbar('Selecione um departamento', 'error');
    return;
  }

  if (createDialog.value.formData.password !== createDialog.value.formData.confirmPassword) {
    showSnackbar('As senhas não coincidem', 'error');
    return;
  }

  createDialog.value.loading = true;

  try {
    const createData = {
      name: createDialog.value.formData.name,
      email: createDialog.value.formData.email,
      password: createDialog.value.formData.password,
      role: createDialog.value.formData.role,
      departmentId: createDialog.value.formData.departmentId, // ✅
      allowedScreens: createDialog.value.formData.allowedScreens,
      status: true // ✅ boolean
    };

    const response = await axios.post(
      `${API_BASE}/user`,
      createData,
      {
        headers: {
          Authorization: `Bearer ${token.value}`,
          'Content-Type': 'application/json'
        }
      }
    );

    Usuarios.value.unshift({
      ...response.data,
      roles: [createData.role],
      allowedScreens: createData.allowedScreens,
      updating: false
    });

    showSnackbar('Usuário criado com sucesso', 'success');
    fecharModalCriacao();

  } catch (error) {
    console.error(error);
    showSnackbar(
      error.response?.data?.message || 'Erro ao criar usuário',
      'error'
    );
  } finally {
    createDialog.value.loading = false;
  }
};


const logout = async () => {
  try {
    await authStore.logout();
    routerInstance.push('/login');
    showSnackbar('Logout realizado com sucesso!', 'success');
  } catch (error) {
    console.error('Erro no logout:', error);
    showSnackbar('Erro ao realizar logout', 'error');
  }
};

// FUNÇÃO TOGGLE STATUS
const toggleUserStatus = async (usuario, newValue) => {
  if (!token.value) {
    showSnackbar('Token de autenticação não encontrado', 'error');
    return;
  }

  const usuarioIndex = Usuarios.value.findIndex(u => u.id === usuario.id);
  if (usuarioIndex === -1) return;

  Usuarios.value[usuarioIndex].updating = true;

  try {
    const statusValue = newValue ? 'true' : 'false';
    
    const statusData = {
      status: statusValue
    };

    console.log('Atualizando status do usuário:', usuario.id, 'para:', statusValue);
    
    const response = await axios.put(
      `${API_BASE}/user/status/${usuario.id}`, 
      statusData,
      {
        headers: {
          'Authorization': `Bearer ${token.value}`,
          'Content-Type': 'application/json'
        }
      }
    );

    if (response.data) {
      Usuarios.value[usuarioIndex] = {
        ...Usuarios.value[usuarioIndex],
        status: statusValue,
        updating: false
      };
      
      showSnackbar(
        `Status do usuário ${usuario.name} atualizado para ${newValue ? 'Ativo' : 'Inativo'}`, 
        'success'
      );
    }

  } catch (error) {
    console.error('Erro ao atualizar status:', error);
    
    Usuarios.value[usuarioIndex].updating = false;
    
    let errorMessage = 'Erro ao atualizar status do usuário';
    
    if (error.response?.status === 401) {
      errorMessage = 'Não autorizado. Faça login novamente.';
    } else if (error.response?.status === 403) {
      errorMessage = 'Sem permissão para atualizar este usuário.';
    } else if (error.response?.status === 404) {
      errorMessage = 'Usuário não encontrado.';
    } else if (error.response?.data?.message) {
      errorMessage = error.response.data.message;
    }
    
    showSnackbar(errorMessage, 'error');
  }
};

// Métodos utilitários para roles
const getRoleColor = (role) => {
  const colorMap = {
    admin: '#F44336',
    manager: '#FF9800',
    user: '#2196F3',
    guest: '#9C27B0'
  };
  return colorMap[role?.toLowerCase()] || '#57C2E3';
};

const getRoleIcon = (role) => {
  const icons = {
    admin: 'fas fa-crown',
    manager: 'fas fa-user-tie',
    user: 'fas fa-user',
    guest: 'fas fa-user-clock'
  };
  return icons[role?.toLowerCase()] || 'fas fa-user';
};

// FUNÇÕES DO MODAL DE EDIÇÃO
const editarUsuario = (usuario) => {
  editDialog.value = {
    show: true,
    loading: false,
    valid: false,
    user: { ...usuario },
    formData: {
      email: usuario.email,
      role: usuario.roles?.[0] || 'user',
      allowedScreens: usuario.allowedScreens || []
    }
  };
};


const fecharModalEdicao = () => {
  editDialog.value = {
    show: false,
    loading: false,
    valid: false,
    user: null,
    formData: {
      email: '',
      role: '',
      allowedScreens: []
    }
  };
};

const toggleScreen = (screenValue) => {
  const index = editDialog.value.formData.allowedScreens.indexOf(screenValue);
  if (index > -1) {
    editDialog.value.formData.allowedScreens.splice(index, 1);
  } else {
    editDialog.value.formData.allowedScreens.push(screenValue);
  }
};



const limparTelas = () => {
  editDialog.value.formData.allowedScreens = [];
};

const salvarEdicao = async () => {
  if (!editDialog.value.valid || !editDialog.value.user?.id) {
    showSnackbar('Por favor, preencha todos os campos obrigatórios', 'error');
    return;
  }

  editDialog.value.loading = true;

  try {
    const updateData = {
      email: editDialog.value.formData.email,
      role: editDialog.value.formData.role,
      allowedScreens: editDialog.value.formData.allowedScreens
    };

    console.log('Dados para atualização:', updateData);

    const response = await axios.put(
      `${API_BASE}/user/${editDialog.value.user.id}`,
      updateData,
      {
        headers: {
          'Authorization': `Bearer ${token.value}`,
          'Content-Type': 'application/json'
        }
      }
    );

    if (response.data) {
      // Atualizar o usuário na lista local
      const usuarioIndex = Usuarios.value.findIndex(u => u.id === editDialog.value.user.id);
      if (usuarioIndex > -1) {
        Usuarios.value[usuarioIndex] = {
          ...Usuarios.value[usuarioIndex],
          email: updateData.email,
          roles: [updateData.role],
          allowedScreens: updateData.allowedScreens
        };
      }

      showSnackbar('Usuário atualizado com sucesso!', 'success');
      fecharModalEdicao();
    }

  } catch (error) {
    console.error('Erro ao atualizar usuário:', error);
    
    let errorMessage = 'Erro ao atualizar usuário';
    
    if (error.response?.status === 401) {
      errorMessage = 'Não autorizado. Faça login novamente.';
    } else if (error.response?.status === 403) {
      errorMessage = 'Sem permissão para editar este usuário.';
    } else if (error.response?.status === 404) {
      errorMessage = 'Usuário não encontrado.';
    } else if (error.response?.data?.message) {
      errorMessage = error.response.data.message;
    }
    
    showSnackbar(errorMessage, 'error');
  } finally {
    editDialog.value.loading = false;
  }
};

// Carregar dados da API
const carregarUsuarios = async () => {
  if (!token.value) {
    showSnackbar('Token de autenticação não encontrado', 'error');
    return;
  }

  loading.value = true;
  try {
    const response = await axios.get(
      `${API_BASE}/all-users`,
      {
        headers: {
          'Authorization': `Bearer ${token.value}`
        }
      }
    );
    
    if (response.data) {
      Usuarios.value = response.data.map(usuario => ({
        ...usuario,
        updating: false,
        roles: usuario.roles || [],
        allowedScreens: usuario.allowedScreens || []
      }));
      
      console.log('Usuários carregados:', Usuarios.value.length);
      showSnackbar(`${response.data.length} usuários carregados com sucesso`, 'success');
    }
  } catch (error) {
    console.error('Erro ao carregar usuários:', error);
    
    let errorMessage = 'Erro ao carregar usuários';
    
    if (error.response?.status === 401) {
      errorMessage = 'Não autorizado. Faça login novamente.';
    } else if (error.response?.status === 500) {
      errorMessage = 'Erro interno do servidor. Tente novamente.';
    } else if (error.response?.data?.message) {
      errorMessage = error.response.data.message;
    }
    
    showSnackbar(errorMessage, 'error');
  } finally {
    loading.value = false;
  }
};

// Lifecycle hooks
onMounted(async () => {
  isMounted.value = true;
  
  if (!token.value) {
    showSnackbar('Acesso negado. Faça login para continuar.', 'error');
    routerInstance.push('/login');
    return;
  }
  await checkAccessAndInitialize();

  setupAxiosInterceptors();
  
  await Promise.all([
    carregarUsuarios(),
    carregarDepartamentos() // ADICIONE ESTA LINHA
    
  ]);
});

onBeforeUnmount(() => {
  isMounted.value = false;
});
</script>

<style scoped>
/* Animations */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes pulse {
  0%, 100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.05);
    opacity: 0.8;
  }
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateX(-20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

/* Main Layout Styles */
.main-content {
  background: linear-gradient(135deg, #E7F2F9 0%, #57C2E3 100%);
  min-height: 100vh;
}

.gradient-text {
  background: linear-gradient(45deg, #57C2E3, #A3C940);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.pulse {
  animation: pulse 2s infinite;
}

/* Header Styles */
.header-btn {
  box-shadow: 0 6px 20px rgba(87, 194, 227, 0.3);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.header-btn:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 25px rgba(87, 194, 227, 0.4);
}

.add-btn {
  box-shadow: 0 6px 20px rgba(163, 201, 64, 0.3);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.add-btn:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 25px rgba(163, 201, 64, 0.4);
}

.status-chip {
  animation: slideIn 0.6s ease-out;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

/* Card Styles */
.modern-card {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.modern-card:hover {
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.1);
}

.card-header {
  border-radius: 12px 12px 0 0;
  position: relative;
  overflow: hidden;
}

.card-header::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(45deg, rgba(255, 255, 255, 0.1), transparent);
  pointer-events: none;
}

.header-icon-wrapper {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  padding: 12px;
  backdrop-filter: blur(10px);
}

.stats-chip {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  font-weight: 600;
}

/* Search Field */
.search-field {
  transition: all 0.3s ease;
}

/* User List Styles */
.user-list-container {
  min-height: 400px;
}

.user-item {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
  border-radius: 0;
  position: relative;
}

.user-item:hover {
  background: linear-gradient(135deg, rgba(87, 194, 227, 0.1), rgba(163, 201, 64, 0.1)) !important;
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.08);
}

.user-item::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  width: 4px;
  background: transparent;
  transition: all 0.3s ease;
}

.user-item:hover::before {
  background: linear-gradient(135deg, #57C2E3, #A3C940);
}

.alternate-bg {
  background: rgba(231, 242, 249, 0.3);
}

.user-avatar-container {
  position: relative;
}

.user-avatar {
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.user-item:hover .user-avatar {
  transform: scale(1.1);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.2);
}

.user-info {
  min-width: 0;
}

.user-name {
  color: #123A5D;
  transition: color 0.3s ease;
}

.user-item:hover .user-name {
  color: #57C2E3;
}

.status-indicator {
  font-weight: 600;
  transition: all 0.3s ease;
}

.roles-container {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
}

.role-chip {
  font-weight: 500;
  transition: all 0.3s ease;
}

.role-chip:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

/* Action Buttons */
.action-buttons {
  opacity: 0.7;
  transition: all 0.3s ease;
}

.user-item:hover .action-buttons {
  opacity: 1;
}

.switch-container {
  display: flex;
  align-items: center;
}

.status-switch {
  transition: all 0.3s ease;
}

/* Status Switch Customization */
.status-switch :deep(.v-switch__track) {
  background-color: #F44336 !important;
  opacity: 1;
}

.status-switch :deep(.v-switch--inset .v-switch__track) {
  background-color: #F44336 !important;
}

.status-switch :deep(.v-switch__thumb) {
  background-color: white !important;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.status-switch :deep(.v-switch--inset.v-selection-control--dirty .v-switch__track) {
  background-color: #4CAF50 !important;
}

.status-switch :deep(.v-selection-control--dirty .v-switch__track) {
  background-color: #4CAF50 !important;
}

.action-btn {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.action-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.15);
}

.edit-btn:hover {
  background-color: rgba(87, 194, 227, 0.1) !important;
}

.details-btn:hover {
  background-color: rgba(163, 201, 64, 0.1) !important;
}

/* Modal Styles */
.modern-edit-card {
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(20px);
  border-radius: 16px;
  overflow: hidden;
}

.edit-card-header {
  position: relative;
}

.edit-card-header::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(45deg, rgba(255, 255, 255, 0.1), transparent);
  pointer-events: none;
}

.close-btn {
  transition: all 0.3s ease;
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.2);
  transform: rotate(90deg);
}

.edit-field {
  transition: all 0.3s ease;
}

/* Screen Cards */
.screen-card {
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: 2px solid transparent;
}

.screen-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
}

.screen-selected {
  border-color: #A3C940;
  background: linear-gradient(135deg, rgba(163, 201, 64, 0.1), rgba(87, 194, 227, 0.1));
}

.screen-selected:hover {
  border-color: #A3C940;
  box-shadow: 0 8px 25px rgba(163, 201, 64, 0.3);
}

/* Modal Buttons */
.cancel-btn {
  transition: all 0.3s ease;
}

.cancel-btn:hover {
  background: rgba(244, 67, 54, 0.1);
  color: #F44336;
}

.save-btn {
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(163, 201, 64, 0.3);
}

.save-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(163, 201, 64, 0.4);
}

/* Empty State */
.empty-state {
  min-height: 300px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.empty-icon {
  animation: fadeInUp 0.6s ease-out;
}

.clear-filter-btn,
.add-first-btn {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.clear-filter-btn:hover,
.add-first-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
}

/* Footer */
.refresh-btn {
  transition: all 0.3s ease;
}

.refresh-btn:hover {
  transform: rotate(180deg);
}

/* Sidebar Styles */
.custom-drawer {
  border-right: none !important;
  box-shadow: 0 0 20px rgba(0, 0, 0, 0.1);
}

.sidebar-profile {
  background: rgba(255, 255, 255, 0.05);
  transition: all 0.3s ease;
}

.profile-avatar {
  transition: all 0.3s ease;
}

.sidebar-profile:hover .profile-avatar {
  transform: scale(1.1);
}

.menu-item {
  margin: 0 12px;
  border-radius: 12px;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.menu-item::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.1), transparent);
  transition: left 0.5s ease;
}

.menu-item:hover::before {
  left: 100%;
}

.menu-item:hover {
  background: rgba(255, 255, 255, 0.1);
  transform: translateX(8px);
}

.active-item {
  background: linear-gradient(135deg, rgba(87, 194, 227, 0.2), rgba(163, 201, 64, 0.2));
  border-left: 4px solid #A3C940;
}

.item-title {
  font-weight: 500;
  transition: all 0.3s ease;
}

.menu-item:hover .item-title {
  font-weight: 600;
  color: #A3C940;
}

.pulse-badge {
  animation: pulse 2s infinite;
}

.logout-btn {
  transition: all 0.3s ease;
  border-color: rgba(255, 255, 255, 0.3);
}

.logout-btn:hover {
  background: rgba(244, 67, 54, 0.1);
  border-color: #F44336;
  color: #F44336;
  transform: translateY(-2px);
}

.user-profile {
  transition: all 0.3s ease;
  padding: 8px 12px;
  border-radius: 12px;
}

.user-profile:hover {
  background: rgba(255, 255, 255, 0.1);
}

/* Responsive Design */
@media (max-width: 960px) {
  .user-item {
    padding: 16px !important;
  }
  
  .action-buttons {
    flex-direction: column;
    gap: 8px !important;
  }
  
  .roles-container {
    margin-top: 12px;
  }
  
  .user-name {
    font-size: 1.1rem;
  }
  
  .modern-edit-card {
    margin: 16px;
    max-width: calc(100vw - 32px);
  }
}

@media (max-width: 600px) {
  .card-header {
    padding: 20px !important;
  }
  
  .search-field {
    margin-bottom: 16px;
  }
  
  .user-item {
    padding: 12px !important;
  }
  
  .user-avatar-container {
    margin-right: 12px !important;
  }
  
  .user-avatar {
    width: 48px !important;
    height: 48px !important;
  }
  
  .edit-card-header {
    padding: 20px !important;
  }
  
  .modern-edit-card {
    margin: 8px;
    max-width: calc(100vw - 16px);
  }

  
}
</style>
